import { onSession, signIn, signInEmail, signInWithQaToken, resetPassword, signOutNow, type Session } from './auth';
import { renderGenerator } from './views/generator';
import { renderAdmin } from './views/admin';
import { initAnalytics, identifyUser } from './analytics';
import { bootQaSessionAndAnalytics } from './qa-token.mjs';

const root = document.getElementById('app')!;
let currentTab: 'generate' | 'admin' = 'generate';

// The QA token leaves the URL BEFORE analytics starts (card nJFP0atO): see
// qa-token.mjs for why the order is the contract, and
// scripts/test/qa-token-before-analytics.test.mjs for the test that holds it.
void bootQaSessionAndAnalytics({
  location,
  history,
  initAnalytics,
  signInWithQaToken,
  onSignInError: (e) => console.error('[qa] signInWithQaToken failed', e),
});

onSession((session) => {
  if (!session) { renderSignIn(); return; }
  if (session.role === null) { renderDenied(session); return; }
  identifyUser(session.user.uid ?? session.user.email ?? 'unknown', session.user.email ?? undefined);
  renderShell(session);
});

function renderSignIn() {
  root.innerHTML = `
    <style>
      .signin-divider { display:flex; align-items:center; gap:12px; width:100%; max-width:320px; color:var(--text-dim); font-size:12px; }
      .signin-divider::before, .signin-divider::after { content:''; flex:1; height:1px; background:var(--border); }
      .email-form { display:flex; flex-direction:column; gap:10px; width:100%; max-width:320px; text-align:left; }
      .email-form input { background:var(--panel-2); color:var(--text); border:1px solid var(--border); border-radius:8px; padding:11px 12px; font-family:inherit; font-size:14px; }
      .email-form input:focus { outline:none; border-color:var(--accent); }
      .email-actions { display:flex; justify-content:space-between; }
      .link-btn { background:none; border:0; color:var(--text-dim); font-size:12px; cursor:pointer; padding:2px; }
      .link-btn:hover { color:var(--text); text-decoration:underline; }
    </style>
    <div class="gate">
      <h2>OpsAgents Studio Console</h2>
      <p>Sign in with your authorized account to generate images.</p>
      <button class="btn-google" id="signInBtn">
        <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5h-1.9V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.1 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.1l6.6 4.8C14.7 15 19 12 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.1 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.1z"/><path fill="#4CAF50" d="M24 44c5.2 0 10-2 13.5-5.2l-6.2-5.2C29.2 35.2 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8L6.1 33C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.2 4.2-4.1 5.6l6.2 5.2C40.9 35.9 44 30.4 44 24c0-1.3-.1-2.3-.4-3.5z"/></svg>
        Sign in with Google
      </button>
      <div class="signin-divider"><span>or</span></div>
      <form class="email-form" id="emailForm">
        <input type="email" id="emailInput" placeholder="you@example.com" autocomplete="email" required />
        <input type="password" id="passwordInput" placeholder="Password (min 6 characters)" autocomplete="current-password" minlength="6" required />
        <button type="submit" class="btn" id="emailSubmit">Sign in with email</button>
        <div class="email-actions">
          <span class="link-btn" style="cursor:default">No account? An admin must invite you.</span>
          <button type="button" class="link-btn" id="forgotBtn">Forgot password?</button>
        </div>
        <div class="error" id="emailError" style="display:none"></div>
      </form>
    </div>
  `;

  const errBox = document.getElementById('emailError') as HTMLDivElement;
  const emailInput = document.getElementById('emailInput') as HTMLInputElement;
  const passwordInput = document.getElementById('passwordInput') as HTMLInputElement;
  const submitBtn = document.getElementById('emailSubmit') as HTMLButtonElement;
  const forgotBtn = document.getElementById('forgotBtn') as HTMLButtonElement;
  const form = document.getElementById('emailForm') as HTMLFormElement;

  const showError = (msg: string) => {
    errBox.style.color = 'var(--danger)';
    errBox.textContent = msg;
    errBox.style.display = 'block';
  };
  const showOk = (msg: string) => {
    errBox.style.color = 'var(--ok)';
    errBox.textContent = msg;
    errBox.style.display = 'block';
  };
  const FRIENDLY_AUTH_ERRORS: Record<string, string> = {
    'auth/invalid-credential': 'Incorrect email or password. If you don’t have an account yet, an admin must invite you.',
    'auth/wrong-password': 'Incorrect email or password. Please try again.',
    'auth/user-not-found': 'No account found with that email — an admin must invite you first.',
    'auth/invalid-email': 'That doesn’t look like a valid email address.',
  };
  const clean = (e: unknown): string => {
    const raw = (e as { code?: string; message?: string } | null);
    // Define m first (falls back to String(e)) so the code can be extracted even
    // when e is a plain string / rejected-promise value with no .message (Gemini PR#11 HIGH).
    const m = raw?.message ?? String(e);
    const code = raw?.code ?? m.match(/\(auth\/[^)]+\)/)?.[0]?.slice(1, -1);
    if (code && FRIENDLY_AUTH_ERRORS[code]) return FRIENDLY_AUTH_ERRORS[code];
    const stripped = m.replace('Firebase: ', '').replace(/\(auth\/[^)]+\)\.?/g, '').trim();
    // Generic fallback when stripping leaves nothing meaningful behind (incl. an
    // arbitrary object that stringifies to '[object Object]').
    return !stripped || stripped === 'Error' || stripped === '[object Object]'
      ? 'Something went wrong. Please try again.'
      : stripped;
  };

  document.getElementById('signInBtn')!.addEventListener('click', () => {
    signIn().catch((e) => showError(clean(e)));
  });

  forgotBtn.addEventListener('click', () => {
    const email = emailInput.value.trim();
    if (!email) { showError('Enter your email above, then click Forgot password.'); return; }
    // Write the trimmed value back before the native validity check so leading/trailing
    // whitespace (mobile autofill) can't fail validation on an otherwise-valid address (Gemini PR#11).
    emailInput.value = email;
    // Validate client-side before spending a Firebase call (and showing its raw error).
    if (!emailInput.validity.valid) { showError('That doesn’t look like a valid email address.'); return; }
    resetPassword(email)
      .then(() => showOk(`Password reset email sent to ${email}.`))
      .catch((e) => showError(clean(e)));
  });

  form.addEventListener('submit', (ev) => {
    ev.preventDefault();
    const email = emailInput.value.trim();
    const password = passwordInput.value;
    if (!email || !password) { showError('Email and password are required.'); return; }
    const originalLabel = submitBtn.textContent;
    submitBtn.disabled = true;
    submitBtn.textContent = 'Signing in…';
    // No public self-signup ([PHASE 2] XDKuaRjc): accounts exist only via the
    // admin invite flow. Unknown emails land in the friendly-error copy below.
    signInEmail(email, password).catch((e) => {
      showError(clean(e));
      submitBtn.disabled = false;
      submitBtn.textContent = originalLabel;
    });
  });
}

function renderDenied(session: Session) {
  root.innerHTML = `
    <div class="gate denied">
      <h2>Not authorized</h2>
      <p>${session.user.email} is not on the allowlist for this app.</p>
      <p>Contact <a href="mailto:michal@opsagents.agency" style="color:var(--accent)">michal@opsagents.agency</a> if you should have access.</p>
      <button class="btn-google" style="background:var(--panel); color:var(--text); border:1px solid var(--border)" id="signOutBtn">Sign out</button>
    </div>
  `;
  document.getElementById('signOutBtn')!.addEventListener('click', () => signOutNow());
}

function renderShell(session: Session) {
  const isAdmin = session.role === 'admin';
  root.innerHTML = `
    <header class="topbar">
      <h1>🎨 OpsAgents Studio Console</h1>
      <div class="user">
        <span>${session.user.email} · <span class="role-${session.role}">${session.role}</span></span>
        <button id="signOutBtn">Sign out</button>
      </div>
    </header>
    <main>
      ${isAdmin ? `
        <div class="tabs">
          <button class="tab ${currentTab === 'generate' ? 'active' : ''}" data-tab="generate">Generate</button>
          <button class="tab ${currentTab === 'admin' ? 'active' : ''}" data-tab="admin">Admin</button>
        </div>
      ` : ''}
      <div id="view"></div>
    </main>
  `;

  document.getElementById('signOutBtn')!.addEventListener('click', () => signOutNow());
  if (isAdmin) {
    root.querySelectorAll<HTMLButtonElement>('.tab').forEach((btn) => {
      btn.addEventListener('click', () => {
        currentTab = btn.dataset.tab as 'generate' | 'admin';
        renderShell(session);
      });
    });
  } else if (currentTab === 'admin') {
    currentTab = 'generate';
  }

  const view = document.getElementById('view')!;
  if (currentTab === 'admin' && isAdmin) renderAdmin(view);
  else renderGenerator(view, { isAdmin });
}
